import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Sparkle, Brain, Path, Compass, ShieldCheck, Star, CaretDown, Stack, Pulse,
} from "@phosphor-icons/react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Disclaimer } from "@/components/Disclaimer";
import { AssessmentCard } from "@/components/AssessmentCard";
import { ConsultBlock } from "@/components/ConsultCTA";
import { useAssessments } from "@/hooks/useAssessments";
import { CATEGORY_ORDER } from "@/lib/constants";

const fadeUp = {
  initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" }, transition: { duration: 0.6, ease: "easeOut" },
};

const HERO_IMG = "https://images.pexels.com/photos/14216814/pexels-photo-14216814.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";

const FAQ = [
  { q: "What is TheInner.app?", a: "A personal intelligence platform. Each free assessment maps a hidden pattern — your attachment style, shadow, self-sabotage, triggers, and more — into one evolving profile that gets more accurate the more you use it." },
  { q: "Are the assessments really free?", a: "Yes. Every assessment gives you a full, detailed result immediately — no paywall. We also email you a copy. There is nothing to buy to see your results." },
  { q: "How are these different from a random online quiz?", a: "They're grounded in real psychology — attachment science, Jungian shadow work, the Big Five, and more — and they build a longitudinal profile plus an AI coach that remembers all your results." },
  { q: "Is this therapy?", a: "No. TheInner.app is an educational and self-reflection tool — not therapy, medical advice, or a diagnosis. It works beautifully alongside professional support." },
  { q: "How does the 1:1 coaching work?", a: "If you want to go deeper, you can book a private 1:1 integration session ($99/hour) with a coach who understands your specific patterns. Everything else on the platform is free." },
];

function FaqItem({ item, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-800" data-testid={`faq-item-${idx}`}>
      <button onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between gap-4 py-5 text-left" data-testid={`faq-toggle-${idx}`}>
        <span className="font-heading text-base font-medium text-slate-100 sm:text-lg">{item.q}</span>
        <CaretDown size={18} className={`shrink-0 text-teal-400 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="pb-5 pr-8 text-sm leading-relaxed text-slate-400">{item.a}</p>}
    </div>
  );
}

export default function Home() {
  const { assessments } = useAssessments();
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 120);
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [location.pathname, location.hash, location.key]);

  const byCat = {};
  assessments.forEach((a) => { (byCat[a.category] = byCat[a.category] || []).push(a); });
  const cats = CATEGORY_ORDER.filter((c) => byCat[c]);

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden grain-overlay">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="h-full w-full object-cover opacity-35" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-slate-950/40" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/40 via-transparent to-slate-950" />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 pb-24 pt-36 sm:px-8 sm:pb-32 sm:pt-44">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-700/70 bg-slate-900/50 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-teal-400 backdrop-blur">
              <Sparkle size={14} weight="fill" /> The operating system for your inner world
            </span>
            <h1 className="mt-6 font-heading text-4xl font-light leading-[1.05] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
              <span className="block">Map the hidden patterns</span>
              <span className="block"><span className="font-medium text-teal-400 text-glow-teal">running your life.</span></span>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">
              {assessments.length || 18} deep, free assessments — attachment, shadow, self-sabotage, triggers, purpose and more — that build one
              living profile of who you really are, and a personalized roadmap for growth that evolves with you.
            </p>
            <div className="mt-9 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
              <a href="#all" className="group inline-flex items-center gap-2 rounded-full bg-teal-500 px-7 py-3.5 text-sm font-semibold text-slate-950 transition-all duration-300 hover:bg-teal-400 hover:shadow-[0_0_36px_rgba(20,184,166,0.45)]" data-testid="hero-cta-btn">
                Explore the Free Assessments <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
              </a>
              <span className="text-sm text-slate-500">Free · No paywall · Results in minutes</span>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-slate-500">
              <span className="flex items-center gap-2">
                <span className="flex -space-x-1.5">{["#F472B6", "#38BDF8", "#FBBF24", "#A78BFA"].map((c) => <span key={c} className="h-6 w-6 rounded-full border-2 border-slate-950" style={{ background: c }} />)}</span>
                Trusted by <span className="font-semibold text-slate-300">12,000+</span> self-explorers
              </span>
              <span className="flex items-center gap-1.5">{[...Array(5)].map((_, i) => <Star key={i} size={14} weight="fill" className="text-amber-400" />)}<span className="ml-1">Grounded in real psychology</span></span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ALL ASSESSMENTS */}
      <section id="all" className="relative scroll-mt-20 border-t border-slate-900 bg-slate-950 py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <motion.div {...fadeUp} className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">{assessments.length || 18} Free Assessments</p>
            <h2 className="mt-4 font-heading text-3xl font-medium tracking-tight text-slate-100 sm:text-4xl">Start with whatever is calling you.</h2>
            <p className="mt-4 text-base leading-relaxed text-slate-400">Each one is a doorway. Take one, take them all — every result feeds the same evolving profile.</p>
          </motion.div>
          {cats.map((cat) => (
            <div key={cat} className="mt-12">
              <h3 className="mb-5 flex items-center gap-2 font-heading text-lg font-medium text-slate-200"><span className="h-1.5 w-1.5 rounded-full bg-teal-400" /> {cat}</h3>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {byCat[cat].map((a, i) => (
                  <motion.div key={a.slug} {...fadeUp} transition={{ duration: 0.5, delay: (i % 3) * 0.06 }}>
                    <AssessmentCard a={a} />
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="scroll-mt-20 border-y border-slate-900 bg-gradient-to-b from-slate-950 to-slate-900/30 py-24">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <motion.div {...fadeUp} className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500">How It Works</p>
            <h2 className="mt-4 font-heading text-3xl font-medium tracking-tight text-slate-100 sm:text-4xl">One profile. Infinite depth over time.</h2>
          </motion.div>
          <div className="mt-12 grid gap-8 lg:grid-cols-3">
            {[
              { icon: Path, t: "Take a free assessment", d: "Pick any of the 12. Answer honestly. Get a rich, detailed result instantly — no paywall, ever." },
              { icon: Stack, t: "Build your living profile", d: "Each assessment adds another layer. Together they form a complete, evolving map of your inner world." },
              { icon: Brain, t: "Grow with your AI coach", d: "Talk to a coach that remembers all your results, take embodiment challenges, and journal your way forward." },
            ].map((s, i) => (
              <motion.div key={i} {...fadeUp} transition={{ duration: 0.6, delay: i * 0.1 }} className="flex gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-teal-400"><s.icon size={22} weight="duotone" /></span>
                <div><h3 className="font-heading text-lg font-medium text-slate-100">{s.t}</h3><p className="mt-1.5 text-sm leading-relaxed text-slate-400">{s.d}</p></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="bg-slate-950 py-24">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <div className="grid gap-6 md:grid-cols-3">
            {[
              { icon: Compass, t: "Real psychology, not horoscopes", d: "Attachment science, Jungian shadow work, the Big Five, and validated frameworks — translated into language that actually moves you." },
              { icon: Pulse, t: "A profile that evolves", d: "Re-take anytime and watch yourself change. Your inner world isn't fixed — and neither is your map of it." },
              { icon: ShieldCheck, t: "Depth with safety", d: "Non-pathologizing, compassionate, and clear. We hold the line between deep self-reflection and clinical care." },
            ].map((c, i) => (
              <motion.div key={i} {...fadeUp} transition={{ duration: 0.6, delay: i * 0.1 }} className="rounded-2xl border border-slate-800 bg-slate-900 p-7">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-teal-500/10 text-teal-400"><c.icon size={24} weight="duotone" /></span>
                <h3 className="mt-5 font-heading text-lg font-medium text-slate-100">{c.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{c.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CONSULT */}
      <section className="border-y border-slate-900 bg-slate-900/30 py-20">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <ConsultBlock source="home" headline="Want a human guide for the deeper work?" sub="The platform is free forever. When you're ready to go deeper, book a private 1:1 integration session with a coach who understands your exact patterns." />
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-slate-950 py-24">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <motion.div {...fadeUp}>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">Questions</p>
            <h2 className="mt-4 font-heading text-3xl font-medium tracking-tight text-slate-100 sm:text-4xl">Before you begin</h2>
          </motion.div>
          <div className="mt-8">{FAQ.map((item, i) => <FaqItem key={i} item={item} idx={i} />)}</div>
          <div className="mt-10"><Disclaimer /></div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
